/*
  HLL visual pass: "Customers are saying" on the home page.

  Rebuilds the Judge.me testimonials carousel into an edge to edge row of
  tall cards: the product photo on top, the review under it. The card in
  the middle is in focus. The ones beside it are the same size but faded.
  Arrows, swipe, or a click on a faded card move the row. It also moves on
  by itself every few seconds until someone touches it.

  Reviews, stars, names, product names and product links all come from the
  Judge.me block already on the page. Nothing is hardcoded here. Judge.me
  does not give a photo, so each product's main photo is looked up from
  Shopify (search first, the product itself as a fallback) and kept for the
  visit. A review whose product has no photo is left out. One review is
  shown per product, so the same photo never appears twice.

  The new row is only built once the photos are known. Until then, and if
  this script or Flickity is missing, the original Judge.me carousel stays.

  Layout: section 16 of assets/hll-visual-pass.css.
  To remove: delete the script tag in layout/theme.liquid and that section.
*/
(function () {
  'use strict';

  var AUTOPLAY = 6000; // ms each review stays in focus. false switches it off.
  var MIN_CARDS = 5; // fewer reviews with a photo than this: keep Judge.me's own carousel
  var PHOTO_CACHE = 'hllHomeReviewPhotos';
  var WIDTHS = [360, 540, 720, 900];

  function text(root, selector) {
    var node = root.querySelector(selector);
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function el(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content) node.textContent = content;
    return node;
  }

  function readCache() {
    try {
      return JSON.parse(window.sessionStorage.getItem(PHOTO_CACHE)) || {};
    } catch (error) {
      return {};
    }
  }

  function writeCache(cache) {
    try {
      window.sessionStorage.setItem(PHOTO_CACHE, JSON.stringify(cache));
    } catch (error) {}
  }

  // "/products/suzie-mocha?x=1" -> "suzie-mocha"
  function handleOf(href) {
    var match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? decodeURIComponent(match[1]) : '';
  }

  function sized(src, width) {
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function getJson(url) {
    return window
      .fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  // Search is a small request and usually enough. It only counts when the
  // result links to the very same product. If search does not have it, ask
  // for the product itself.
  function findPhoto(review) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var search =
      root +
      'search/suggest.json?q=' +
      encodeURIComponent(review.product) +
      '&resources[type]=product&resources[limit]=6&resources[options][fields]=title';

    return getJson(search)
      .then(function (json) {
        var products = (json && json.resources && json.resources.results && json.resources.results.products) || [];
        for (var i = 0; i < products.length; i++) {
          var product = products[i];
          if (product.handle !== review.handle && handleOf(product.url) !== review.handle) continue;
          return product.image || (product.featured_image && product.featured_image.url) || '';
        }
        return '';
      })
      .then(function (src) {
        if (src) return src;
        return getJson(root + 'products/' + encodeURIComponent(review.handle) + '.js').then(function (product) {
          return (product && product.featured_image) || '';
        });
      })
      .then(function (src) {
        if (src && src.indexOf('//') === 0) src = 'https:' + src;
        return src || '';
      });
  }

  function readReviews(widget) {
    var seen = {};
    return Array.prototype.slice
      .call(widget.querySelectorAll('.jdgm-testimonial'))
      .map(function (item) {
        var link = item.querySelector('.jdgm-product-name a');
        var href = link ? link.getAttribute('href') || '' : '';
        return {
          stars: item.querySelector('.jdgm-rating-section .jdgm-stars') || item.querySelector('.jdgm-stars'),
          body: text(item, '.jdgm-text'),
          name: text(item, '.jdgm-reviewer-name'),
          product: link ? link.textContent.replace(/\s+/g, ' ').trim() : '',
          href: href,
          handle: handleOf(href)
        };
      })
      .filter(function (review) {
        if (!review.body || !review.handle || !review.product || seen[review.handle]) return false;
        seen[review.handle] = true;
        return true;
      });
  }

  // Looks up every photo, four at a time, then calls done
  function loadPhotos(reviews, done) {
    var cache = readCache();
    var queue = reviews.slice();
    var running = 0;
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      writeCache(cache);
      reviews.forEach(function (review) {
        review.photo = cache[review.handle] || '';
      });
      done();
    }

    function next() {
      if (!queue.length) {
        if (!running) finish();
        return;
      }
      var review = queue.shift();
      if (Object.prototype.hasOwnProperty.call(cache, review.handle)) {
        next();
        return;
      }
      running++;
      findPhoto(review).then(function (src) {
        cache[review.handle] = src;
        running--;
        next();
      });
    }

    for (var i = 0; i < 4; i++) next();
  }

  function build(widget, reviews) {
    var heading = text(widget, '.jdgm-title') || 'Customers are saying';
    var section = el('section', 'hll-reviews hll-reviews--focus');
    section.setAttribute('aria-label', heading);

    var header = el('div', 'hll-reviews__header');
    // Same class as the other home page titles, so it takes their style
    header.appendChild(el('h2', 'section-header__title', heading));

    // Overall rating, copied from the Judge.me header: "4.9 from 168 reviews"
    var ratingLabel = widget.querySelector('.jdgm-header .jdgm-stars');
    var score = ratingLabel ? parseFloat(ratingLabel.getAttribute('aria-label')) : NaN;
    if (!score) score = parseFloat(text(widget, '.jdgm-rating-text'));
    var count = parseInt(text(widget, '.jdgm-reviews-count').replace(/[^\d]/g, ''), 10);
    if (score && count) {
      var summary = el('div', 'hll-reviews__summary');
      var star = el('span', 'hll-reviews__stars hll-reviews__summary-star');
      star.setAttribute('aria-hidden', 'true');
      star.appendChild(el('span', 'jdgm-star jdgm--on'));
      summary.appendChild(star);
      summary.appendChild(el('span', '', (Math.round(score * 10) / 10).toFixed(1) + ' from ' + count + ' reviews'));
      header.appendChild(summary);
    }
    section.appendChild(header);

    var slider = el('div', 'hll-reviews__slider');
    var cards = [];

    reviews.forEach(function (review) {
      var cell = el('div', 'hll-reviews__cell');
      var card = el('figure', 'hll-story');

      // Product photo, linked to the product
      var photo = el('a', 'hll-story__photo');
      photo.href = review.href;
      photo.setAttribute('aria-label', 'View ' + review.product);
      var img = document.createElement('img');
      img.alt = review.product;
      img.width = 480;
      img.height = 720;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.sizes = '(min-width: 769px) 26vw, 66vw';
      img.srcset = WIDTHS.map(function (width) {
        return sized(review.photo, width) + ' ' + width + 'w';
      }).join(', ');
      img.src = sized(review.photo, 720);
      photo.appendChild(img);
      card.appendChild(photo);

      var copy = el('div', 'hll-story__text');
      if (review.stars) {
        var stars = review.stars.cloneNode(true);
        stars.className = 'hll-reviews__stars';
        copy.appendChild(stars);
      }
      copy.appendChild(el('blockquote', 'hll-story__body', review.body));

      var by = el('figcaption', 'hll-story__by');
      if (review.name) by.appendChild(el('span', 'hll-story__name', review.name));
      var about = el('a', 'hll-story__product', 'Review of ');
      about.href = review.href;
      about.appendChild(el('span', 'hll-story__product-name', review.product));
      by.appendChild(about);
      copy.appendChild(by);
      card.appendChild(copy);

      cell.appendChild(card);
      slider.appendChild(cell);
      cards.push(card);
      review.links = [photo, about];
    });

    section.appendChild(slider);

    var nav = el('div', 'hll-reviews__nav');
    var prev = el('button', 'hll-reviews__arrow hll-reviews__arrow--prev');
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Previous review');
    var next = el('button', 'hll-reviews__arrow hll-reviews__arrow--next');
    next.type = 'button';
    next.setAttribute('aria-label', 'Next review');
    nav.appendChild(prev);
    nav.appendChild(next);
    section.appendChild(nav);

    // Place it outside the theme's page-width box so it can run edge to edge
    var box = widget.closest('.page-width') || widget.closest('.shopify-app-block') || widget;
    box.parentElement.insertBefore(section, box);
    widget.classList.add('hll-reviews-ready');

    // Reviews differ in length. The row is as tall as the longest card.
    function sizeRow() {
      var tallest = 0;
      cards.forEach(function (card) {
        tallest = Math.max(tallest, card.offsetHeight);
      });
      if (tallest) section.style.setProperty('--hll-card-h', tallest + 'px');
    }

    sizeRow();

    var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var flkty = new window.Flickity(slider, {
      autoPlay: calm ? false : AUTOPLAY,
      cellAlign: 'center',
      dragThreshold: 8,
      pageDots: false,
      pauseAutoPlayOnHover: true,
      prevNextButtons: false,
      wrapAround: true
    });

    prev.addEventListener('click', function () {
      flkty.previous(true);
      if (flkty.stopPlayer) flkty.stopPlayer();
    });
    next.addEventListener('click', function () {
      flkty.next(true);
      if (flkty.stopPlayer) flkty.stopPlayer();
    });

    // A click on a faded card brings it into focus. Its links only work
    // once it is the one in focus.
    slider.addEventListener(
      'click',
      function (event) {
        var cell = event.target.closest ? event.target.closest('.hll-reviews__cell') : null;
        if (cell && !cell.classList.contains('is-selected')) event.preventDefault();
      },
      true
    );
    flkty.on('staticClick', function (event, pointer, cellElement, cellIndex) {
      if (typeof cellIndex === 'number' && cellIndex !== flkty.selectedIndex) flkty.select(cellIndex);
    });

    // Keyboard: only the card in focus can be tabbed to
    function syncFocus() {
      reviews.forEach(function (review, index) {
        review.links.forEach(function (link) {
          if (index === flkty.selectedIndex) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
      });
    }
    flkty.on('select', syncFocus);
    syncFocus();

    function refit() {
      sizeRow();
      flkty.resize();
    }

    var resizeTimer;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(refit, 150);
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refit);
  }

  function start(widget) {
    var reviews = readReviews(widget);
    if (reviews.length < MIN_CARDS) return;

    var begun = false;

    // Start the photo lookups only when the section is getting close
    function checkNear() {
      if (begun) return;
      var rect = widget.getBoundingClientRect();
      if (rect.top > window.innerHeight + 1500 || rect.bottom < -1500) return;
      begun = true;
      window.removeEventListener('scroll', checkNear);
      loadPhotos(reviews, function () {
        var ready = reviews.filter(function (review) {
          return review.photo;
        });
        if (ready.length >= MIN_CARDS) build(widget, ready);
      });
    }

    window.addEventListener('scroll', checkNear, { passive: true });
    checkNear();
  }

  function boot() {
    if (!document.body.classList.contains('template-index')) return;
    var tries = 0;
    (function look() {
      var widget = document.querySelector('.jdgm-testimonials-carousel');
      if (widget && widget.classList.contains('hll-reviews-ready')) return;
      if (widget && widget.querySelector('.jdgm-testimonial .jdgm-text') && window.Flickity) {
        start(widget);
        return;
      }
      // Judge.me can fill the block in after the page loads, so keep looking for 20s
      if (++tries < 80) window.setTimeout(look, 250);
    })();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
